import { ReportType, type ReportDto, type ReportSummaryMetric } from "@edlab-fms/shared";
import { formatMoney } from "../../lib/format/money";

export function monthHeading(periodEnd: string): string {
  const date = new Date(`${periodEnd}T12:00:00`);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

export function throughLabel(periodEnd: string): string {
  const date = new Date(`${periodEnd}T12:00:00`);
  return `Through ${date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}`;
}

export function preparedLabel(preparedAt: string): string {
  const date = new Date(preparedAt);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function formatReportDateCell(value: string): string {
  const date = new Date(`${value}T12:00:00`);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export function formatReportAmount(value: string): string {
  const amount = Number.parseFloat(value);
  if (!Number.isFinite(amount)) {
    return value;
  }
  return amount.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function summaryValue(
  summary: ReportSummaryMetric[],
  key: string,
  asMoney = true,
): string {
  const entry = summary.find((item) => item.key === key);
  if (!entry) {
    return "—";
  }
  return asMoney ? formatMoney(entry.value) : entry.value;
}

export function reportSubtitle(report: ReportDto): string {
  if (report.meta.reportType === ReportType.DONOR) {
    return report.meta.filterSummary.Donor ?? "Donor report";
  }
  if (report.meta.filterSummary.Group) {
    return `Programmes in ${report.meta.filterSummary.Group}`;
  }
  const scopeMatch = report.meta.filterSummary.Scope?.match(/^(\d+)/);
  const unitCount = scopeMatch ? Number.parseInt(scopeMatch[1]!, 10) : Number.NaN;
  if (Number.isFinite(unitCount) && unitCount < 8) {
    return "Assigned programmes only";
  }
  return "All programmes";
}

export function reportFooterNote(report: ReportDto): string {
  if (report.meta.reportType === ReportType.DONOR) {
    return "Utilization reflects actual posted payments. Unspent funds may include amounts already approved for future payment.";
  }
  return "Utilization reflects actual posted payments. Net movement excludes opening balances and is not a bank balance.";
}

export function primaryStats(report: ReportDto): ReportSummaryMetric[] {
  if (report.meta.reportType === ReportType.DONOR) {
    const order = ["openingUnspent", "periodReceived", "periodUtilized", "closingUnspent"];
    return order
      .map((key) => report.summary.find((entry) => entry.key === key))
      .filter((entry): entry is ReportSummaryMetric => Boolean(entry));
  }

  const order = ["ytdIncome", "ytdPaid", "periodNet", "ytdNet"];
  return order
    .map((key) => report.summary.find((entry) => entry.key === key))
    .filter((entry): entry is ReportSummaryMetric => Boolean(entry));
}

export function isAmountColumn(column: string): boolean {
  const label = column.toLowerCase();
  return (
    label.includes("amount") ||
    label.includes("paid") ||
    label.includes("budget") ||
    label.includes("income") ||
    label.includes("received") ||
    label.includes("utilized") ||
    label.includes("net") ||
    label.includes("award") ||
    label.includes("allocated") ||
    label.includes("balance")
  );
}
